import type { CaptchaState } from '../auth/useCapCaptcha';

/**
 * Tiny status badge for the programmatic Cap captcha. Captcha is invisible
 * by design (no checkbox), but we surface a low-key indicator so the user
 * knows why their submit may take an extra few hundred ms when they're
 * very fast and the proof-of-work hasn't finished yet.
 *
 * Renders nothing in the `disabled`/`idle` states so it stays out of the
 * way when there's nothing useful to say.
 */
export function CaptchaStatusBadge({ state }: { state: CaptchaState }) {
  if (state.status === 'disabled' || state.status === 'idle') return null;

  if (state.status === 'solving') {
    const pct = Math.max(5, Math.min(100, Math.round(state.progress)));
    return (
      <div aria-live="polite" className="text-xs text-slate-500">
        <div className="flex items-center justify-between mb-1">
          <span>Verifying you're human…</span>
          <span className="tabular-nums text-slate-400">{pct}%</span>
        </div>
        <div className="h-1 w-full bg-slate-200 rounded">
          <div
            className="h-1 bg-indigo-500 rounded transition-[width] duration-150"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  if (state.status === 'ready') {
    return (
      <div className="text-xs text-emerald-600" aria-live="polite">
        Verification complete.
      </div>
    );
  }

  // status === 'error'
  return (
    <div className="text-xs text-red-600" aria-live="polite">
      Captcha verification failed{state.error ? `: ${state.error}` : '.'}
    </div>
  );
}
